import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import BASE_API_URL from "../../apis/index";
import { Box, Typography, Divider, Tabs, Tab, Chip, Alert, Button, Grid, Breadcrumbs, Link, CardContent, CircularProgress } from "@mui/material";
import Carousel from "react-bootstrap/Carousel"; // Bootstrap Carousel
import "bootstrap/dist/css/bootstrap.min.css"; // Bootstrap styles
import CheckIcon from "@mui/icons-material/Check";
import CompProductsSectionIngManf from "./CompProductsSectionIngManf";
import CompProductsSectionIngDist from "./CompProductsSectionIngDist";
import CompServicesSection from "./CompServicesSection";
import CompServicesSectionCRO from "./CompServicesSectionCRO";

export default function CompanyDetails({ admin }) {
  const [company, setCompany] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  const useQuery = () => new URLSearchParams(useLocation().search);
  const compId = useQuery().get("compId");

  useEffect(() => {
    async function fetchCompanyDetails() {
      try {
        const authToken = localStorage.getItem("authToken");
        const res = await axios.get(
          `${BASE_API_URL}/api/buyer/company/${compId}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        setCompany(res.data);
      } catch (err) {
        console.error("Failed to fetch company data", err);
      }
    }
    fetchCompanyDetails();
  }, [compId]);

  // 📱 Mask mobile numbers
  const maskMobile = (num) => {
    // Remove spaces
    let clean = num.replace(/\s+/g, "");
    if (clean.startsWith("+")) {
      // Keep country code + first digit + last digit
      const cc = clean.slice(0, 3); // e.g. +91
      return `${cc} ${clean[3]}********${clean[clean.length - 1]}`;
    } else {
      // No country code
      return `${clean[0]}********${clean[clean.length - 1]}`;
    }
  };

  // 📧 Mask emails
  const maskEmail = (email) => {
    const [name, domain] = email.split("@");
    if (name.length <= 2) {
      return `${name[0]}*@${domain}`;
    }
    return `${name[0]}${"*".repeat(name.length - 2)}${name[name.length - 1]}@${domain}`;
  };

  if (!company) return <Typography>Loading...</Typography>;

  const companyTypes = JSON.parse(company.company_types || "[]");

  return (
    <Wrapper className="container">
      <Breadcrumbs aria-label="breadcrumb" className='mb-4 mt-2 pageBreadCrumbs bg-light px-4 py-2 rounded'>
        <Link underline="hover" color="inherit" href="/"> Home </Link>
        <Typography sx={{ color: 'text.primary' }}> {company.company_name} </Typography>
      </Breadcrumbs>

      <div className="p-3 mb-4">
        <div className="FlexRow">
          <div className="LeftSide">
            <div className="CompLogo">
              <img
                src={`${BASE_API_URL}/uploads/user_comp_docs/${company.company_logo}`}
                alt="Company Logo"
              />
            </div>
            <Typography variant="h5" className="txt_color_primary">
              {company.company_name}
            </Typography>
            <Box mt={1} mb={1}>
              {companyTypes.map((type) => (
                <Chip
                  key={type}
                  label={type}
                  color="warning"
                  variant="outlined"
                  sx={{ mr: 1 }}
                  className="rounded"
                />
              ))}
            </Box>

            <Typography variant="body2" mt={2}> GST Number: {company.gst_number} </Typography>
            <Alert icon={<CheckIcon fontSize="inherit" />} severity="warning" className="mt-3" style={{ width: '320px' }} > Status: <strong>{company.status}</strong> </Alert>
          </div>

          <div className="RightSide">
            {company.company_images && (
              <Carousel>
                {JSON.parse(company.company_images).map((img, idx) => (
                  <Carousel.Item key={idx}>
                    <img
                      className="d-block w-100"
                      src={`${BASE_API_URL}/uploads/user_comp_docs/${img}`}
                      alt={`company-img-${idx}`}
                      style={{
                        maxHeight: "300px",
                        objectFit: "cover",
                        borderRadius: "10px",
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            )}
          </div>
        </div>
      </div>

      <Divider />

      <div className="TabSection border rounded-5">
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          indicatorColor="secondary"
          textColor="secondary"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
          }}
        >
          <Tab label="Company Info" />
          <Tab label="Statutory Details" />
          {admin && <Tab label="Bank Details" />}
          <Tab label="Vendor Details" />
        </Tabs>

        <Box mt={1}>
          {tabIndex === 0 && (
            <div className="px-md-5 px-3">
              <div className="mb-2"> <span className="fw-medium txt_color_primary"> Brief: </span> {company.brief}</div>
              <div className="mb-2"> <span className="fw-medium txt_color_primary"> Address: </span> {company.address}</div>
              <div className="mb-2"> <span className="fw-medium txt_color_primary"> Country: </span> {company.country_of_origin}</div>
              <div className="mb-2">
                <span className="fw-medium txt_color_primary"> Mobile: </span>
                {JSON.parse(company.mobile_numbers)
                  .map((num, i, arr) => (
                    <span key={i}>
                      {admin ? num : maskMobile(num)}
                      {i < arr.length - 1 && ", "}
                    </span>
                  ))}
              </div>

              <Typography>
                <span className="fw-medium txt_color_primary"> Email: </span>
                {JSON.parse(company.emails)
                  .map((em, i, arr) => (
                    <span key={i}>
                      {admin ? em : maskEmail(em)}
                      {i < arr.length - 1 && ", "}
                    </span>
                  ))}
              </Typography>
            </div>
          )}

          {tabIndex === 1 && (
            <div className="px-md-5 px-3">
              <Typography>PAN: {company.pan_number}</Typography>
              <Typography mt={1}>GST: {company.gst_number}</Typography>
              <Typography mt={1}>CIN: {company.cin_code}</Typography>

              <Box mt={2}>
                <Grid container spacing={1}>
                  {company.pan_doc && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        className='w-100'
                        variant="outlined"
                        color="primary"
                        href={`${BASE_API_URL}/uploads/user_comp_docs/${company.pan_doc}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        PAN Document (PDF)
                      </Button>
                    </Grid>
                  )}

                  {company.gst_doc && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        className='w-100'
                        variant="outlined"
                        color="primary"
                        href={`${BASE_API_URL}/uploads/user_comp_docs/${company.gst_doc}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        GST Document (PDF)
                      </Button>
                    </Grid>
                  )}

                  {company.cin_doc && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        className='w-100'
                        variant="outlined"
                        color="primary"
                        href={`${BASE_API_URL}/uploads/user_comp_docs/${company.cin_doc}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        CIN Document (PDF)
                      </Button>
                    </Grid>
                  )}

                  {company.rc_doc && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        className='w-100'
                        variant="outlined"
                        color="primary"
                        href={`${BASE_API_URL}/uploads/user_comp_docs/${company.rc_doc}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        RC Document (PDF)
                      </Button>
                    </Grid>
                  )}

                  {company.llp_cert && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        className='w-100'
                        variant="outlined"
                        color="primary"
                        href={`${BASE_API_URL}/uploads/user_comp_docs/${company.llp_cert}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LLP Certificate (PDF)
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </div>
          )}

          {admin && tabIndex === 2 && (
            <div className="px-md-5 px-3">
              <Typography> Account Holder: {company.account_holder_name} </Typography>
              <Typography> Account Number: {company.account_number} </Typography>
              <Typography> Bank: {company.bank} </Typography>
              <Typography> Branch: {company.branch} </Typography>
              <Typography> IFSC: {company.ifsc} </Typography>
            </div>
          )}

          {tabIndex === (admin ? 3 : 2) && (
            <Box className='px-4'>
              <VendorDetailsTab userId={company.user_id} />
            </Box>
          )}
        </Box>
      </div>

      {company.status === "Approved" && companyTypes.includes("Ingredient Manufacturer") && (
        <CompProductsSectionIngManf
          compId={company.id}
          compName={company.company_name}
        />
      )
      }

      {company.status === "Approved" && companyTypes.includes("Ingredient Distributor") && (
        <CompProductsSectionIngDist
          compId={company.id}
          compName={company.company_name}
        />
      )}

      {company.status === "Approved" && companyTypes.includes("Contract Manufacturer") && (
        <CompServicesSection compId={company.id} />
      )}
      
      {company.status === "Approved" && companyTypes.includes("CRO") && (
        <CompServicesSectionCRO compId={company.id} />
      )}
    </Wrapper>
  );
}

function VendorDetailsTab({ userId }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        const res = await axios.get(`${BASE_API_URL}/api/user/user-profile/${userId}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });

        setProfile(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  if (loading) return <CircularProgress sx={{ display: 'block', margin: '50px auto' }} />;
  if (error) return <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>;

  if (!profile) return null;

  return (
    <VendorDetailsTabWrapper>
      <Grid container spacing={2}>
        <Grid>
          <img src={`${BASE_API_URL}/uploads/user_profile_images/${profile.profile_image}`} alt={profile.name} className="profileImage" />
        </Grid>

        {/* Profile Details */}
        <Grid>
          <CardContent>
            <Typography variant="h5" gutterBottom className='txt_color_primary'>{profile.name}</Typography>
            <Typography variant="subtitle1" className="txt_color_secondary">{profile.designation}</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}> <strong>Company:</strong> {profile.company} </Typography>
            <Typography variant="body1"> <strong>Email:</strong> {profile.email} </Typography>
            <Typography variant="body1"> <strong>Mobile:</strong> {profile.mobile} </Typography>
          </CardContent>
        </Grid>
      </Grid>
    </VendorDetailsTabWrapper>
  );
}

const Wrapper = styled.div`
  padding: 20px;
  min-height: 85vh;
  background: white;
  border-radius: 15px;
  .CompLogo{
    img {
      width: 100px;
      height: 100px;
      object-fit: contain;
    }
  }
  .TabSection{ min-height: 320px; }
  .LeftSide{ flex: 1; }
  .RightSide{ flex: 1; }
  .FlexRow {
    display: flex;
    gap: 20px;
    align-items: flex-start;
  }
`;

const VendorDetailsTabWrapper = styled.div`
img.profileImage{
  border-radius: 10px;
  width: 200px;
  height: auto;
  object-fit: cover;
}
`;